<template>
  <div
    :id="containerId"
    class="tm-video-modal-wistia"
  ></div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, useId } from '@/composition/vue/compositionApi'
import { getMonitoringLoggerService } from '@/core/container/ioc'
import { TmWistiaUnavailableError } from '@/core/error/tmWistiaUnavailableError'
import { LogLevel } from '@/core/logger/types'
import { loadScript } from '@/utils/script'
import type { WistiaVideo } from '@/components/shared/types'

const MEDIA_ERR_ABORTED = 1

const props = defineProps<{
  videoId: string
}>()

// No `wistia_async_*` class: Wistia's DOM scanner throws when such a container is removed before its deferred embed
const containerId = `tm-video-modal-wistia-${useId()}`

let videoInstance: WistiaVideo | undefined
let embeddedVideo: WistiaVideo | undefined
let isUnmounted = false
let scriptsLoadPromise: Promise<[boolean, boolean]> | undefined
const { promise: videoReadyPromise, resolve: resolveVideoReady } = Promise.withResolvers<WistiaVideo>()

const logWistiaWarning = (error: Error) => {
  getMonitoringLoggerService().logAndCapture(error, LogLevel.WARNING, 'wistia')
}

const isIgnorableWistiaScriptError = (error: Error) => error.message.startsWith('Script loading aborted:')

const isIgnorableMediaError = (mediaError: MediaError | null) => mediaError?.code === MEDIA_ERR_ABORTED

const isPopoverVisible = () => videoInstance?.popover?.isVisible() ?? false

const pauseIfPopoverHidden = () => {
  if (!isPopoverVisible()) {
    videoInstance?.pause()
  }
}

const onPopoverHide = () => {
  videoInstance?.pause()
}

const onEscKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    videoInstance?.pause()
  }
}

const isOurWistiaVideoError = (target: EventTarget | null) => {
  if (!(target instanceof HTMLVideoElement)) {
    return false
  }

  return target.closest(`#${containerId}, #${containerId}_popover_container`) !== null
}

const onNativeVideoError = (event: Event) => {
  if (!isOurWistiaVideoError(event.target)) {
    return
  }

  const mediaError = (event.target as HTMLVideoElement).error
  event.stopPropagation()

  if (isIgnorableMediaError(mediaError)) {
    return
  }

  const message = mediaError ? `Wistia MediaError: code ${mediaError.code}` : 'Wistia MediaError: unknown'
  logWistiaWarning(new Error(message))
}

const setupVideoInstance = (video: WistiaVideo) => {
  videoInstance = video
  video.bind('popoverhide', onPopoverHide)
  video.bind('play', pauseIfPopoverHidden)

  if (video.state() === 'playing' && !isPopoverVisible()) {
    video.pause()
  }
}

const embedVideo = () => {
  if (embeddedVideo || isUnmounted || !window.Wistia) {
    return
  }

  const video = window.Wistia.embed(props.videoId, {
    container: containerId,
    popover: true,
    popoverContent: 'link',
  })
  embeddedVideo = video
  video.ready(() => {
    if (isUnmounted) {
      return
    }
    setupVideoInstance(video)
    resolveVideoReady(video)
  })
}

const loadWistiaScripts = () => {
  if (!scriptsLoadPromise) {
    scriptsLoadPromise = Promise.all([
      loadScript(`https://fast.wistia.com/embed/medias/${props.videoId}.jsonp`),
      loadScript('https://fast.wistia.com/assets/external/E-v1.js'),
    ]).catch((e: unknown) => {
      scriptsLoadPromise = undefined
      const error = e instanceof Error ? e : new Error(String(e))
      if (!isIgnorableWistiaScriptError(error)) {
        logWistiaWarning(error)
      }
      throw error
    })
  }

  return scriptsLoadPromise
}

const getVideoInstance = async (): Promise<WistiaVideo> => {
  if (videoInstance) {
    return videoInstance
  }

  try {
    await loadWistiaScripts()
  } catch {
    throw new TmWistiaUnavailableError('Wistia video is unavailable')
  }

  embedVideo()
  return videoReadyPromise
}

onMounted(() => {
  document.addEventListener('keydown', onEscKeydown)
  document.addEventListener('error', onNativeVideoError, true)

  loadWistiaScripts()
    .then(embedVideo)
    .catch(() => undefined)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKeydown)
  document.removeEventListener('error', onNativeVideoError, true)
  isUnmounted = true

  if (videoInstance) {
    videoInstance.pause()
    videoInstance.unbind('popoverhide', onPopoverHide)
    videoInstance.unbind('play', pauseIfPopoverHidden)
  }
  embeddedVideo?.remove()
})

defineExpose({
  getVideoInstance,
})
</script>

<style lang="scss" scoped>
.tm-video-modal-wistia,
:deep(.wistia_click_to_play) {
  position: absolute !important;
  inset: 0;
  z-index: 2;
  width: unset !important;
  height: unset !important;
  cursor: pointer;
}
</style>
